/**
 * ProjectSelector (FE-3 §6.1)
 *
 * Trigger controllato: riceve progetti/scope e notifica tramite callback.
 *  - Trigger button con icona/colore/nome del progetto corrente (o "Inbox");
 *  - Popover con ricerca testuale, lista progetti attivi con task_count;
 *  - Voce distinta "Task senza progetto";
 *  - Azioni "Nuovo progetto" e "Gestisci progetti";
 *  - Tastiera: Esc chiude, frecce su/giù navigano, Enter seleziona;
 *  - Stati vuoti: caricamento, nessun progetto, ricerca senza risultati.
 *
 * Nessun preferito nella prima release.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  Folder,
  Inbox as InboxIcon,
  Plus,
  Search,
  Settings2,
} from "lucide-react";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import type { TaskProject, TaskScope } from "../../types/tasks";

const ICON_MAP: Record<string, typeof Folder> = {
  folder: Folder,
  sparkles: Folder, // mapping minimale: l'icona reale la decide il project.icon
  default: Folder,
};

function ProjectIcon({
  icon,
  color,
  className,
}: {
  icon?: string;
  color: string;
  className?: string;
}) {
  const Lucide = (icon && ICON_MAP[icon]) || Folder;
  return (
    <span
      className={`inline-flex items-center justify-center rounded-md ${className ?? ""}`}
      style={{ backgroundColor: `${color}22`, color }}
    >
      <Lucide className="h-3.5 w-3.5" />
    </span>
  );
}

export interface ProjectSelectorProps {
  /** Progetti attivi del tenant (no archiviati). */
  projects: TaskProject[];
  /** Scope corrente. */
  scope: TaskScope | null;
  /** Conteggio task inbox (facoltativo, dal summary o projects loading). */
  inboxCount?: number;
  /** Loading globale (es. primo caricamento). */
  loading?: boolean;
  /** Selezione scope (progetto o inbox). */
  onScopeChange: (scope: TaskScope) => void;
  /** Apri form nuovo progetto. */
  onCreateProject: () => void;
  /** Apri sheet gestione progetti. */
  onManageProjects: () => void;
}

export default function ProjectSelector({
  projects,
  scope,
  inboxCount,
  loading = false,
  onScopeChange,
  onCreateProject,
  onManageProjects,
}: ProjectSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Quando apro il popover, focus sulla search e reset stato.
  useEffect(() => {
    if (open) {
      setSearch("");
      setActiveIndex(0);
      // Defer focus per aspettare il mount del content.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
    );
  }, [projects, search]);

  // Flat list per la navigazione tastiera: inbox + progetti filtrati + azioni.
  const flatItems = useMemo(() => {
    const items: Array<
      | { kind: "inbox" }
      | { kind: "project"; project: TaskProject }
      | { kind: "action-new" }
      | { kind: "action-manage" }
    > = [{ kind: "inbox" }, ...filteredProjects.map((p) => ({ kind: "project" as const, project: p }))];
    // Le azioni sono sempre visibili ma NON partecipano alla ricerca testuale:
    // includiamo solo new/manage quando il pannello è aperto.
    items.push({ kind: "action-new" });
    items.push({ kind: "action-manage" });
    return items;
  }, [filteredProjects]);

  // Clamp activeIndex quando cambia la lista.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, flatItems.length - 1));
  }, [flatItems.length]);

  const currentProject =
    scope?.kind === "project"
      ? projects.find((p) => p.id === scope.projectId)
      : null;
  const isInbox = scope?.kind === "inbox";

  const triggerLabel = currentProject?.name ?? "Task senza progetto";
  const triggerColor = currentProject?.color ?? "#7b3fff";
  const triggerIcon = currentProject?.icon;

  const handleSelect = (idx: number) => {
    const item = flatItems[idx];
    if (!item) return;
    if (item.kind === "inbox") {
      onScopeChange({ kind: "inbox" });
      setOpen(false);
    } else if (item.kind === "project") {
      onScopeChange({ kind: "project", projectId: item.project.id });
      setOpen(false);
    } else if (item.kind === "action-new") {
      onCreateProject();
      setOpen(false);
    } else if (item.kind === "action-manage") {
      onManageProjects();
      setOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(activeIndex);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Scroll attivo in view quando cambia.
  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex min-w-0 max-w-full items-center gap-2 h-10 px-3 rounded-md bg-[var(--hexa-card-bg)] border border-[rgba(106,77,255,0.25)] hover:border-[var(--hexa-purple)] transition text-sm font-medium text-[var(--hexa-text-primary)]"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Selettore progetto"
        >
          {isInbox ? (
            <InboxIcon className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
          ) : (
            <ProjectIcon icon={triggerIcon} color={triggerColor} />
          )}
          <span className="truncate max-w-[160px]">{triggerLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 max-w-[calc(100vw-24px)] max-h-[80dvh] overflow-y-auto p-0 bg-[var(--hexa-surface)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)]"
        onKeyDown={handleKeyDown}
      >
        {/* Search */}
        <div className="p-2 border-b border-[var(--hexa-border-subtle)]">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
            <Input
              ref={inputRef}
              placeholder="Cerca progetto..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setActiveIndex(0);
              }}
              className="h-7 pl-8 pr-2 bg-[var(--hexa-card-bg)] border-[rgba(106,77,255,0.25)] text-sm rounded-md"
            />
          </div>
        </div>

        {/* Lista */}
        <div
          ref={listRef}
          role="listbox"
          aria-label="Progetti"
          className="max-h-72 overflow-y-auto scrollbar-hexa"
        >
          {/* Inbox */}
          <SelectorRow
            idx={0}
            activeIndex={activeIndex}
            isActive={isInbox}
            onSelect={handleSelect}
            icon={<InboxIcon className="h-3.5 w-3.5" />}
            color="var(--hexa-text-muted)"
            label="Task senza progetto"
            badge={inboxCount}
          />

          {loading && (
            <div className="px-3 py-2 text-xs text-[var(--hexa-text-muted)]">
              Caricamento progetti...
            </div>
          )}

          {!loading && filteredProjects.length === 0 && search.trim() && (
            <div className="px-3 py-4 text-xs text-center text-[var(--hexa-text-muted)]">
              Nessun progetto trovato
            </div>
          )}

          {filteredProjects.map((p, i) => {
            const idx = i + 1; // inbox occupa 0
            const isActive =
              scope?.kind === "project" && scope.projectId === p.id;
            return (
              <SelectorRow
                key={p.id}
                idx={idx}
                activeIndex={activeIndex}
                isActive={isActive}
                onSelect={handleSelect}
                icon={<ProjectIcon icon={p.icon} color={p.color} />}
                color={p.color}
                label={p.name}
                badge={p.task_count}
              />
            );
          })}
        </div>

        {/* Azioni */}
        <div className="border-t border-[var(--hexa-border-subtle)]">
          <button
            type="button"
            data-idx={flatItems.length - 2}
            onMouseEnter={() => setActiveIndex(flatItems.length - 2)}
            onClick={() => handleSelect(flatItems.length - 2)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)] transition"
          >
            <Plus className="h-3.5 w-3.5 text-[var(--hexa-purple)]" />
            Nuovo progetto
          </button>
          <button
            type="button"
            data-idx={flatItems.length - 1}
            onMouseEnter={() => setActiveIndex(flatItems.length - 1)}
            onClick={() => handleSelect(flatItems.length - 1)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)] transition"
          >
            <Settings2 className="h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
            Gestisci progetti
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface SelectorRowProps {
  idx: number;
  activeIndex: number;
  isActive: boolean;
  onSelect: (idx: number) => void;
  icon: React.ReactNode;
  color: string;
  label: string;
  badge?: number;
}

function SelectorRow({
  idx,
  activeIndex,
  isActive,
  onSelect,
  icon,
  color,
  label,
  badge,
}: SelectorRowProps) {
  const isFocused = idx === activeIndex;
  return (
    <button
      type="button"
      role="option"
      aria-selected={isActive}
      data-idx={idx}
      onMouseEnter={() => {
        // Imposta focus solo se diverso, per non scatenare re-render inutili.
        // Non usiamo setActiveIndex qui per evitare loop hover→focus→scroll.
      }}
      onClick={() => onSelect(idx)}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition ${
        isActive
          ? "bg-[var(--hexa-purple-glow)] text-[var(--hexa-text-primary)]"
          : isFocused
          ? "bg-[var(--hexa-surface-hover)] text-[var(--hexa-text-primary)]"
          : "text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
      }`}
    >
      <span
        className="inline-flex items-center justify-center"
        style={{ color }}
      >
        {icon}
      </span>
      <span className="flex-1 text-left truncate">{label}</span>
      {typeof badge === "number" && (
        <span
          className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-medium"
          style={{
            backgroundColor: `${color}22`,
            color,
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}
